import { createPortal } from 'react-dom'
import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { useApi } from '../../hooks/useApi'
import CostEvolutionChart from './CostEvolutionChart'
import { featureLabel } from './constants'
import type { ProjectCostData } from './ProjectCostCard'

interface ProjectCostModalProps {
  project: ProjectCostData
  onClose: () => void
}

const PERIODS = [
  { value: '1d', label: "Aujourd'hui" },
  { value: '7d', label: '7 jours' },
  { value: '30d', label: '30 jours' },
] as const

export default function ProjectCostModal({ project, onClose }: ProjectCostModalProps) {
  const [period, setPeriod] = useState<string>('30d')

  const { data: dailyData } = useApi<{ dailyCosts: Array<{ date: string; calls: number; credits: number }> }>(
    `/api/costs/project/${project.clientId}?period=${period}`
  )

  const avgUnitCost = project.features.length > 0
    ? project.features.reduce((sum, f) => sum + f.unitCost, 0) / project.features.length
    : 0.002

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onEsc)
    return () => window.removeEventListener('keydown', onEsc)
  }, [onClose])

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl border border-[#E2E4ED] bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#E2E4ED] bg-[#1B2559] px-6 py-4 rounded-t-2xl">
          <div>
            <h2 className="text-lg font-semibold text-white">{project.clientName}</h2>
            <p className="text-sm text-indigo-300">{project.clientDomain}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-white/10 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Period selector */}
        <div className="flex gap-2 px-6 pt-4">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                period === p.value
                  ? 'bg-indigo-500 text-white'
                  : 'bg-[#F4F5FA] text-[#8B8FA7] hover:text-[#1B2559]'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Features table */}
        <div className="px-6 pt-4">
          <h3 className="mb-3 text-sm font-medium text-[#8B8FA7]">Détail par feature</h3>
          <div className="overflow-hidden rounded-lg border border-[#E2E4ED]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E2E4ED] bg-[#F8F9FC]">
                  <th className="px-4 py-2.5 text-left font-medium text-[#8B8FA7]">Feature</th>
                  <th className="px-4 py-2.5 text-right font-medium text-[#8B8FA7]">Appels</th>
                  <th className="px-4 py-2.5 text-right font-medium text-[#8B8FA7]">Coût unitaire</th>
                  <th className="px-4 py-2.5 text-right font-medium text-[#8B8FA7]">Coût total</th>
                </tr>
              </thead>
              <tbody>
                {project.features.map((f) => (
                  <tr key={f.action} className="border-b border-[#E2E4ED]/50 last:border-0">
                    <td className="px-4 py-2.5 font-medium text-[#1B2559]">{featureLabel(f.action)}</td>
                    <td className="px-4 py-2.5 text-right text-[#555A73]">{f.calls.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right text-[#8B8FA7]">${f.unitCost.toFixed(4)}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-[#1B2559]">${f.estimatedCost.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-[#F8F9FC]">
                  <td className="px-4 py-2.5 font-semibold text-[#1B2559]">Total</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-[#1B2559]">{project.totalCalls.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right text-[#8B8FA7]">-</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-indigo-600">${project.estimatedCost.toFixed(4)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Evolution chart */}
        <div className="px-6 py-4">
          <h3 className="mb-3 text-sm font-medium text-[#8B8FA7]">Évolution jour par jour</h3>
          <CostEvolutionChart
            data={dailyData?.dailyCosts ?? []}
            unitCost={avgUnitCost}
            height={220}
          />
        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}
